// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import usePromotionCodeForm from "@insite/client-framework/Common/Hooks/usePromotionCodeForm";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPromotionsDataView } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import applyPromotion from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/ApplyPromotion";
import removePromotion from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/RemovePromotion";
import removePromotions from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/RemovePromotions";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link from "@insite/mobius/Link";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import Tag from "@insite/mobius/Tag";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { LinkPresentationProps } from "@rpsc/MobiusOverrides/Link/Link";
import TextField, { TextFieldPresentationProps } from "@rpsc/MobiusOverrides/TextField";
import StyledBox from "@rpsc/XDL/Components/Common/Box";
import { mergeWithCss } from "@rpsc/XDL/Utilities";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";

export interface PromoBoxStyles {
    wrapper?: LoadingOverlayProps;
    container?: InjectableCss;
    enterPromotionCodeContainer?: InjectableCss;
    enterPromotionCodeTextField?: TextFieldPresentationProps;
    enterPromotionCodeApplyButton?: ButtonPresentationProps;
    success?: TypographyPresentationProps;
    promotionsContainer?: InjectableCss;
    promoLineContainer?: InjectableCss;
    promoName?: TypographyPresentationProps;
    removeAll?: LinkPresentationProps;
    title?: TypographyPresentationProps;
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        promotionErrorMessage: state.pages.checkoutReviewAndSubmit.promotionErrorMessage,
        promotions: getCurrentPromotionsDataView(state).value,
        isCartLoading: getCurrentCartState(state).isLoading,
    };
};

const mapDispatchToProps = {
    applyPromotion,
    removePromotion,
    removePromotions,
};

interface ownProps {
    extendedStyles?: PromoBoxStyles;
}
export const promoBoxStyles: PromoBoxStyles = {};

type Props = ownProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const PromoBox: React.FC<Props> = ({
    extendedStyles,
    promotionErrorMessage,
    promotions,
    applyPromotion,
    removePromotion,
    removePromotions,
    isCartLoading,
}) => {
    const styles = mergeWithCss(promoBoxStyles, extendedStyles);
    const { promotionCode, setPromotionCode, isUpdatingPromotions, setIsUpdatingPromotions, setRemovingPromotionCode } =
        usePromotionCodeForm();
    const [lastSuccessPromo, setLastSuccessPromo] = useState("");
    const dispatch = useDispatch();

    const cleanup = () => {
        dispatch({
            type: "Pages/CheckoutReviewAndSubmit/ClearMessages",
        });
        setLastSuccessPromo("");
    };

    useEffect(() => {
        cleanup();
    }, []);

    const applyPromotionHandler = (e?: any) => {
        e?.preventDefault();
        e?.stopPropagation();
        cleanup();
        setIsUpdatingPromotions(true);
        applyPromotion({
            promotionCode,
            onComplete: (
                result: Partial<{
                    successMessage: string;
                    errorMessage: string;
                    isPromotionAppliedSuccess: boolean;
                }>,
            ) => {
                if (result.isPromotionAppliedSuccess) {
                    setLastSuccessPromo(promotionCode);
                    setPromotionCode("");
                }
                setIsUpdatingPromotions(false);
            },
        });
    };

    const removePromo = (id: string, promotionCode: string) => {
        cleanup();
        setIsUpdatingPromotions(true);
        removePromotion({
            promotionId: id,
            onComplete: () => {
                setIsUpdatingPromotions(false);
            },
        });
        setRemovingPromotionCode(promotionCode);
    };

    const removeAllPromo = () => {
        cleanup();
        removePromotions();
    };

    return (
        <LoadingOverlay {...styles.wrapper} loading={isUpdatingPromotions && !isCartLoading}>
            <StyledBox {...styles.container}>
                <Typography {...styles.title}>{translate("Have a Promotional Code?")}</Typography>
                <StyledWrapper {...styles.enterPromotionCodeContainer}>
                    <TextField
                        onChange={event => {
                            setPromotionCode(event.target.value);
                        }}
                        onKeyDown={event => {
                            if (event.keyCode === 13) {
                                event.preventDefault();
                                event.stopPropagation();
                                applyPromotionHandler(event);
                            }
                        }}
                        value={promotionCode}
                        label={translate("Promotion Code")}
                        placeholder={translate("Enter Code")}
                        error={promotionErrorMessage}
                        isOptional
                        {...styles.enterPromotionCodeTextField}
                    />
                    <Button
                        onClick={applyPromotionHandler}
                        disabled={!(promotionCode?.length >= 2)}
                        {...styles.enterPromotionCodeApplyButton}
                        aria-label={translate("Apply")}
                    >
                        {translate("Apply")}
                    </Button>
                </StyledWrapper>
                {!!lastSuccessPromo && (
                    <Typography {...styles.success} data-test-selector="promo-status-message">
                        {`${lastSuccessPromo} ${translate("successfully applied!")}`}
                    </Typography>
                )}
                {!!promotions?.length && (
                    <StyledWrapper {...styles.promotionsContainer}>
                        {promotions?.map(({ name, promotionCode, id }) => (
                            <Tag
                                onDelete={() => removePromo(id, promotionCode)}
                                key={name}
                                data-test-selector="applied-promo-codes"
                            >
                                {name}
                            </Tag>
                        ))}
                        <Link {...styles.removeAll} onClick={removeAllPromo} aria-label={translate("Remove All Codes")}>
                            {translate("Remove All Codes")}
                        </Link>
                    </StyledWrapper>
                )}
            </StyledBox>
        </LoadingOverlay>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(PromoBox);
